/*
 * App Router version of `../updateDefaultShipmentHandler`.
 */
import { NextResponse, type NextRequest } from 'next/server'
import storefrontApi from 'api/storefront-api'
import { createLogger } from 'shared-utils'
import { setRequestLocale } from '../../../common/api/setRequestLocale'
import { routeHandlerWithLogging } from '../../../logging/routeHandlerWithLogging'
import { buildDefaultShipmentPayload } from './buildDefaultShipmentPayload'
import { type UpdateDefaultShipmentHandlerOpts, type UpdateDefaultShipmentRequest } from './types'

const logger = createLogger('updateDefaultShipmentRouteHandler')

/**
 * POST /api/orders/update-default-shipment — persist the FE-driven delivery
 * selection onto an existing UPFRONT order when the store has no third-party
 * carrier configured (DEFAULT shipping branch).
 *
 * 1. GET the persisted order by `orderNumber` (the PUT replaces rather than
 *    merges, so everything we do not intend to change has to round-trip).
 * 2. Merge the selection in — see `buildDefaultShipmentPayload`, which owns
 *    the PICKUP/DELIVERY branching.
 * 3. PUT.
 *
 * Payment-provider agnostic — per-provider cart updates (Svea cart shipping
 * line, etc.) go through `updateOrder` separately on the same tick.
 */
export const updateDefaultShipmentRouteHandler = (opts?: UpdateDefaultShipmentHandlerOpts) =>
  routeHandlerWithLogging(async function POST(request: NextRequest) {
    setRequestLocale(request)

    try {
      const { orderNumber, defaultDetails, shippingCost } =
        (await request.json()) as UpdateDefaultShipmentRequest

      if (!orderNumber) {
        return NextResponse.json({ error: 'orderNumber is required.' }, { status: 400 })
      }
      if (!defaultDetails) {
        return NextResponse.json({ error: 'defaultDetails is required.' }, { status: 400 })
      }

      const { data: existing, error: getErr } = await storefrontApi.orders.getStorefrontOrder({
        orderNumber
      })

      if (getErr || !existing) {
        return NextResponse.json(
          { error: typeof getErr === 'string' ? getErr : 'order-not-found' },
          { status: 404 }
        )
      }

      const payload = buildDefaultShipmentPayload({
        existing,
        orderNumber,
        defaultDetails,
        shippingCost,
        customerId: opts?.customerId
      })

      logger.log('BE REQ PAYLOAD', payload)

      return NextResponse.json(await storefrontApi.orders.updateStorefrontOrder(payload))
    } catch (e) {
      return NextResponse.json(
        { error: e instanceof Error ? e.message : 'Unknown error' },
        { status: 400 }
      )
    }
  })
